import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { IFeaturedTestimonialBlockProps } from "./FeaturedTestimonialBlock.props";

export const FeaturedTestimonialBlock = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  parentClass,
  spacing,
  testimonial,
}: IFeaturedTestimonialBlockProps) => {
  if (!eyebrow && !heading && !description && !testimonial?.quote) return null;
  const Link = getComponent("Link");

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-12 md:gap-16">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          layout="rail"
        />
        {testimonial ? (
          <figure
            className={cn(
              "grid border border-border md:grid-cols-[minmax(16rem,0.85fr)_1.15fr]",
              !testimonial.portrait && "md:grid-cols-1",
            )}
          >
            {testimonial.portrait ? (
              <DaylightMedia
                className="min-w-0 border-0 border-r-border md:border-r"
                imageClassName="object-cover"
                media={testimonial.portrait.image}
                sizes="(min-width: 768px) 38vw, calc(100vw - 48px)"
              />
            ) : null}
            <div className="flex min-w-0 flex-col justify-center p-8 md:p-12 lg:p-16">
              <blockquote>
                <DaylightRichText
                  className="font-daylight-display text-3xl font-bold leading-[1.05] tracking-[-0.04em] text-graphite md:text-4xl lg:text-5xl"
                  content={testimonial.quote}
                />
              </blockquote>
              <figcaption className="mt-10 flex flex-wrap items-baseline gap-x-6 gap-y-2 border-t border-border pt-5">
                <p className="daylight-label text-graphite">{testimonial.name}</p>
                {testimonial.attribution ? (
                  <p className="daylight-label-micro text-northsea">{testimonial.attribution}</p>
                ) : null}
              </figcaption>
              {testimonial.link ? (
                <Link
                  aria-label={testimonial.link.accessibilityLabel || undefined}
                  className="daylight-label mt-6 inline-flex min-h-11 items-center gap-2 self-start text-primary transition-colors hover:text-graphite focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  href={testimonial.link.href}
                  rel={testimonial.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={testimonial.link.openInNewTab ? "_blank" : undefined}
                >
                  {testimonial.link.label}
                  <span aria-hidden="true">↗</span>
                </Link>
              ) : null}
            </div>
          </figure>
        ) : null}
      </div>
    </DaylightSection>
  );
};

FeaturedTestimonialBlock.displayName = "Block::FeaturedTestimonialBlock";
